import { z } from 'zod';

/**
 * Shared Zod schemas + inferred types for the Drop-2 two-endpoint create flow.
 *
 * This module is a **faithful transcription** of the frozen wire contract in
 * ADR-0024 (`docs/adr/0024-api-json-contract.md`, story A1, amended 2026-06-11)
 * — it does not re-decide the contract. One source of truth that both the API
 * handlers (S05) and the SPA client (S11) import, so they validate against the
 * **same** definition (frontend-architecture R9 / security SR10). Every exported
 * type is `z.infer<>` of its schema — never hand-declared in parallel.
 *
 * The two endpoints (ADR-0024):
 *   - chat turn    — `POST /api/v1/workstreams/create/chat` —
 *                    `{ messages, question, questionCreatedAt? }`
 *                    → `{ data: { userMessage, reply }, meta }`. Repeatable;
 *                    creates nothing; **never returns a `workstreamId`**. Both
 *                    `userMessage` (echoed user turn) and `reply` carry a
 *                    server-stamped RFC-3339 `createdAt`.
 *   - explicit create — `POST /api/v1/workstreams/create` — `{ messages }`
 *                    → `{ data: { workstreamId, createdAt, reply }, meta }`. The
 *                    **only** place a `workstreamId` is minted (by S05, fabricated,
 *                    stateless — nothing here generates or persists it).
 *
 * The `{ data, meta }` envelope on both responses is a **deliberate BFF write
 * deviation recorded by ADR-0024** — NOT something PLAN §4.2 mandates for writes
 * (§4.2 returns a bare resource for writes). The envelope is modelled because
 * ADR-0024 says so.
 */

/**
 * Bounds. ADR-0024 requires the transcript to be **bounded in the S02 schema**
 * (the 413 row delegates the exact bound here) so an oversized body is rejected
 * at the edge before it reaches the ~10 MB gateway ceiling — an unbounded
 * `messages[]` / free-text field is a denial-of-service vector (OWASP API4).
 * These caps are generous for a create-flow conversation (100 turns × 10 KB ≈
 * 1 MB worst case) while closing the unbounded-input hole.
 *
 * **Exported** so the client enforces the SAME limits in the UI (disable send +
 * inline feedback) before a request is rejected server-side — one source of truth
 * for both sides (the schema is the runtime gate; these are the literal bounds).
 */
export const MAX_MESSAGES = 100;
export const MAX_TEXT_LEN = 10_000;

/**
 * Bounds for the agent-proposed workstream identity (ADR-0044). One source of
 * truth for the chat-response surface, the create request, and the persisted
 * record — mirrored by the pydantic `AgentTurnOutput` field caps.
 */
export const MAX_WS_TITLE_LEN = 80;
export const MAX_WS_DESCRIPTION_LEN = 300;

/**
 * Bound for {@link MessageSchema}'s `agent` speaker label — a registry agent name
 * (`discovery`, `engineer`, …), not free text. Mirrored by the pydantic `Message`.
 */
export const MAX_AGENT_NAME_LEN = 64;

/**
 * RFC-3339 **UTC** timestamp (`Z`-suffixed). One reusable refinement so both
 * consumers validate `createdAt` identically — rejects a naked epoch (non-string)
 * and a local-offset string (e.g. `+10:00`), accepts only the `Z`-terminated UTC
 * form. Kills the "frontend says valid, backend says no" desync (R9). Zod 4's
 * `z.iso.datetime()` defaults to `offset: false, local: false` = UTC `Z` only.
 *
 * Declared **above** {@link MessageSchema} because that schema now references it
 * (Zod schemas are runtime values — the const must initialise first).
 */
export const Rfc3339DateTime = z.iso.datetime();
export type Rfc3339DateTime = z.infer<typeof Rfc3339DateTime>;

/**
 * A single turn in the client-held transcript (chat-companion shape, ADR-0024).
 * `role` is exactly `user | assistant` (ADR-0024 §"names fixed by an external
 * spec"); strict so unknown keys on a message are rejected at the edge.
 * `content` is non-empty and length-capped (see {@link MAX_TEXT_LEN}).
 *
 * `createdAt` (optional, RFC-3339 UTC) is the message's display timestamp — added
 * **additively** per ADR-0024 §7 ("if a server timestamp is ever added it is
 * additive and RFC-3339"). The server stamps it on every message it returns; on
 * an inbound transcript message it may carry a client time or be omitted. Because
 * it is optional, existing bodies that omit it still pass; `strictObject` still
 * rejects any OTHER unknown key.
 *
 * `agent` (optional, ≤ {@link MAX_AGENT_NAME_LEN}) is the **name of the agent that
 * produced an assistant turn** (e.g. `discovery`, `engineer`) — the transcript's
 * speaker label. Absent on user turns and on every message persisted before this
 * field existed, so consumers must treat it as "unknown speaker", never as a
 * default. Additive + optional per ADR-0024 §7, exactly like `createdAt` above:
 * both wire ends reject unknown keys (`z.strictObject` here, `extra="forbid"` in
 * the pydantic mirror) and the `ui` / `workstreamagent` modules deploy
 * independently, so the field must be **accepted by both before any producer
 * emits it** — otherwise whichever module deploys second 422s every turn.
 */
export const MessageSchema = z.strictObject({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(MAX_TEXT_LEN),
  createdAt: Rfc3339DateTime.optional(),
  agent: z.string().min(1).max(MAX_AGENT_NAME_LEN).optional(),
});
export type Message = z.infer<typeof MessageSchema>;

/**
 * Attachment caps. The base64 length bounds the synchronous Lambda invoke
 * payload (6 MB cap) and keeps the decoded PDF under Bedrock Converse's
 * 4.5 MB per-document limit (both verified against AWS docs 2026-07-13):
 * 4.5M base64 chars ≈ 3.4 MB raw + a full 1 MB transcript still fits.
 */
export const MAX_ATTACHMENT_B64_LEN = 4_500_000;
/** Raw-byte sibling (¾ of the base64 cap) for the SPA's pre-encode file check. */
export const MAX_ATTACHMENT_BYTES = 3_375_000;

/**
 * A PDF riding inline on a chat turn. Turn-scoped: the agent runtime feeds it
 * to the model as a Bedrock Converse document block (via pydantic-ai
 * `BinaryContent`) — no tool loop, no storage.
 * `data` is standard base64; the refine checks the `%PDF-` magic bytes so a
 * mislabelled file is rejected at the trust boundary, in the SPA pre-send
 * and at the BFF edge alike (`atob` is global in browsers and Node ≥ 16).
 *
 * **This is a readability mechanism, not the storage model** (Drop 9). Since
 * the composer paperclip also uploads every attachment through the Drop-8
 * ledger flow (ADR-0050), the file itself is durable and shows in the Files
 * rail. The inline copy exists solely because `read_artifact` returns text and
 * cannot extract PDF content, so this is an agent's only way to read a PDF —
 * which is also why the type stays pinned to `application/pdf`: markdown needs
 * no inline copy, it is readable straight from the ledger.
 */
// ponytail: base64-on-the-wire is now PDF-readability only — persistence moved
// to the ledger (ADR-0050). Retire this path entirely if `read_artifact` ever
// learns PDF text extraction (the sidecar-at-finalize note in ADR-0041 §4).
export const AttachmentSchema = z.strictObject({
  name: z.string().min(1).max(255),
  mediaType: z.literal('application/pdf'),
  data: z
    .base64()
    .min(16)
    .max(MAX_ATTACHMENT_B64_LEN)
    .refine((d) => {
      // Zod runs refinements even when the base64 check above failed, and
      // atob throws on invalid input — treat "won't decode" as "not a PDF".
      try {
        return atob(d.slice(0, 8)).startsWith('%PDF-');
      } catch {
        return false;
      }
    }, 'not a PDF (magic bytes)'),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/* ── Chat-turn pair — POST /api/v1/workstreams/create/chat ───────────────── */

/**
 * Chat-turn request. Chat-companion-shaped: `messages` is the full client-held
 * transcript and **may be empty on the first turn**; `question` is the user's
 * new message (non-empty, ADR-0024). Strict — unknown keys are rejected.
 *
 * `questionCreatedAt` (optional, RFC-3339 UTC) is the client's **send-time** for
 * this new `question` turn — the moment the user hit Send. The server stamps the
 * echoed user message with this value, or fills `now` when absent ("client
 * send-time, server fills if absent"). A scalar sibling of the bare-string
 * `question` — `question` itself stays exactly as ADR-0024 froze it (not promoted
 * to a Message). Additive + optional (ADR-0024 §7/§11).
 *
 * `workstreamId` (optional, UUID) is the **client-minted** id for the
 * workstream-to-be: the SPA generates it once per companion conversation and
 * resends it on every pre-create chat turn (and on the eventual create), so the
 * agent pipeline accumulates discovery state (mode, summary, artifact ledger)
 * under ONE id instead of minting a throwaway per turn. Additive + optional.
 */
export const CreateChatRequestSchema = z.strictObject({
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
  question: z.string().min(1).max(MAX_TEXT_LEN),
  questionCreatedAt: Rfc3339DateTime.optional(),
  workstreamId: z.uuid().optional(),
  /**
   * PDF attached to THIS pre-create turn (additive-optional, ADR-0024 §7) —
   * forwarded as-is to `AgentInvokeRequest.attachment`, exactly as the
   * post-create `/workstreams/chat` turn does. Pre-create there is no workstream
   * row for an upload to belong to (`resolveOwned` 404s until create), so the
   * inline copy is the ONLY way the discovery agent can read a file attached in
   * the create panel — without it it answers "I don't have access to any
   * artifacts" while holding the file the user just handed it. Turn-scoped: not
   * persisted, not echoed; the staged file still reaches the ledger at create.
   */
  attachment: AttachmentSchema.optional(),
});
export type CreateChatRequest = z.infer<typeof CreateChatRequestSchema>;

/**
 * Chat-turn response payload — the **echoed, server-stamped user turn**
 * (`userMessage`) plus the assistant's deterministic scripted `reply`. Both
 * carry a server-resolved `createdAt`, so the client renders both bubbles from
 * the server's clock (the authoritative source) rather than its own. `userMessage`
 * echoes the new `question` as a `Message` (role `user`) with `createdAt` =
 * the client's `questionCreatedAt` or the server's fill. A chat turn still creates
 * nothing: the response is strict and never carries a `workstreamId` — the
 * client-minted id only becomes a persisted workstream at explicit create.
 * `userMessage` is an additive field (ADR-0024 §7/§11).
 */
export const CreateChatDataSchema = z.strictObject({
  userMessage: MessageSchema,
  reply: MessageSchema,
  /**
   * Agent-proposed workstream identity (ADR-0044, additive-optional per
   * ADR-0024 §7/§11): present once the discovery agent has understood the ask
   * well enough to name it. The SPA auto-creates the workstream on first
   * arrival — a chat turn still creates nothing server-side.
   */
  workstreamTitle: z.string().min(1).max(MAX_WS_TITLE_LEN).optional(),
  workstreamDescription: z.string().min(1).max(MAX_WS_DESCRIPTION_LEN).optional(),
});
export type CreateChatData = z.infer<typeof CreateChatDataSchema>;

/* ── Explicit-create pair — POST /api/v1/workstreams/create ──────────────── */

/**
 * Explicit-create request — the complete final transcript, **non-empty with at
 * least one user message**, and **no `question` field** (strict — a request
 * carrying `question` is rejected). The `.some(user)` refinement covers both
 * the empty-transcript and no-user-message 422 cases.
 *
 * `workstreamId` (optional, UUID) is the same client-minted id the companion
 * chat turns carried — when present the handler persists under it (instead of
 * minting server-side), so the discovery state the pipeline accumulated during
 * the pre-create interview belongs to the created workstream. Tenant safety is
 * unchanged: the DynamoDB PK still comes exclusively from verified token claims.
 */
export const CreateWorkstreamRequestSchema = z.strictObject({
  messages: z
    .array(MessageSchema)
    .max(MAX_MESSAGES)
    .refine((messages) => messages.some((m) => m.role === 'user'), {
      message: 'transcript must be non-empty and contain at least one user message',
    }),
  workstreamId: z.uuid().optional(),
  /**
   * Agent-proposed workstream identity (ADR-0044, additive-optional): the
   * `workstreamTitle`/`workstreamDescription` the chat response carried, echoed
   * back on create. Preferred over the derive-from-first-message fallback.
   * Display strings only — never used for identity or tenancy.
   */
  title: z.string().min(1).max(MAX_WS_TITLE_LEN).optional(),
  description: z.string().min(1).max(MAX_WS_DESCRIPTION_LEN).optional(),
});
export type CreateWorkstreamRequest = z.infer<typeof CreateWorkstreamRequestSchema>;

/**
 * Explicit-create response payload — the minted `workstreamId` and an RFC-3339
 * UTC `createdAt`. The **only** place a `workstreamId` appears.
 *
 * `reply` is **optional and no longer emitted**. It used to carry the Drop-2
 * scripted confirmation ("Your workstream is set up and ready…"), which the SPA
 * appended to the transcript directly beneath the live agent's genuine discovery
 * question — the product claiming to be finished while the agent was still
 * asking. Create is a persistence step, not a conversational turn: the agent's
 * own reply already arrived on the chat turn that triggered the auto-create.
 * The field stays in the schema (optional) rather than being removed because
 * `/api/v1` evolves additively (AC10) — nothing produces it now, and nothing
 * may assume it is present.
 */
export const CreateWorkstreamDataSchema = z.strictObject({
  workstreamId: z.string().min(1),
  createdAt: Rfc3339DateTime,
  reply: MessageSchema.optional(),
});
export type CreateWorkstreamData = z.infer<typeof CreateWorkstreamDataSchema>;

/* ── Envelope + meta (shared) ───────────────────────────────────────────── */

/**
 * Response-side schemas are **tolerant-out** (ADR-0024 §11): the client reads
 * with `z.looseObject` so a future additive field never breaks an old client,
 * and additive/pagination/diagnostic fields (which ADR-0024 §4 puts in `meta`,
 * and the RFC-9457 `requestId`/`errors` extensions, §6) **survive** the parse
 * rather than being silently stripped. The `data` payloads, by contrast, stay
 * strict (fixed shapes; additions live in `meta`) — see above.
 */

/**
 * The `meta` block carried by every success envelope — `requestId` for
 * correlation, plus room for diagnostic / pagination fields (tolerant-out).
 */
export const MetaSchema = z.looseObject({
  requestId: z.string(),
});
export type Meta = z.infer<typeof MetaSchema>;

/** `{ data, meta }` envelope for the chat turn (ADR-0024 BFF write deviation; tolerant-out). */
export const CreateChatResponseSchema = z.looseObject({
  data: CreateChatDataSchema,
  meta: MetaSchema,
});
export type CreateChatResponse = z.infer<typeof CreateChatResponseSchema>;

/** `{ data, meta }` envelope for the explicit create (ADR-0024 BFF write deviation; tolerant-out). */
export const CreateWorkstreamResponseSchema = z.looseObject({
  data: CreateWorkstreamDataSchema,
  meta: MetaSchema,
});
export type CreateWorkstreamResponse = z.infer<typeof CreateWorkstreamResponseSchema>;

/* ── Errors — RFC-9457 application/problem+json ─────────────────────────── */

/**
 * RFC-9457 problem object (ADR-0024 §6). The five members are named exactly per
 * the RFC (`type`, `title`, `status`, `detail`, `instance`); **tolerant-out**
 * (`z.looseObject`, ADR-0024 §11) because RFC-9457 problem objects are extensible
 * — the `requestId` + `errors` extension members the client consumes (§6) must
 * **survive** the parse, not be stripped. `status` mirrors the HTTP status line.
 */
export const ProblemDetailsSchema = z.looseObject({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string(),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;
